/** L7 accounting, tax, payroll and Indian PAN Q&A — deterministic, source-led. */
import { ACCOUNTING_INDUSTRIES, ACCOUNTING_SERVICES } from "./index";
import { ACCOUNTING_EDITORIAL } from "./editorial";
import { liftFlatToFloor } from "../_qa/topup-merge";
import { ACCOUNTING_TOPUP_AXIS } from "../_qa/topup-axes-2";
import {
  ACCOUNTING_QUOTE_NOTE,
  ACCOUNTING_VOLATILE_NOTE,
  PAN_CARD_NOTE,
} from "./facts";

export type AccountingQA = { slug: string; q: string; a: string; synonyms: string[]; serviceSlug?: string };
const ASOF = `${ACCOUNTING_VOLATILE_NOTE} ${ACCOUNTING_QUOTE_NOTE}`;

const CORE: AccountingQA[] = [
  {
    slug: "accounting-records-responsibility",
    q: "จ้างสำนักงานบัญชีแล้วกรรมการยังต้องรับผิดชอบข้อมูลบัญชีหรือไม่",
    a: `ยังต้องรับผิดชอบในการจัดให้มีเอกสารและข้อมูลที่ครบถ้วนตามข้อเท็จจริง การจ้างผู้ทำบัญชีหรือที่ปรึกษาไม่ทำให้หน้าที่ของนิติบุคคลและผู้มีหน้าที่จัดทำบัญชีสิ้นสุด ควรกำหนดผู้ส่งเอกสาร วันตัดรอบ และขั้นตอนอนุมัติรายการไว้เป็นลายลักษณ์อักษร อ้างอิงพระราชบัญญัติการบัญชี พ.ศ. 2543 และข้อมูลกรมพัฒนาธุรกิจการค้า (dbd.go.th) ${ASOF}`,
    synonyms: ["กรรมการรับผิดชอบบัญชี", "จ้างสำนักงานบัญชีแล้วใครรับผิด"],
  },
  {
    slug: "tax-deadlines-online-paper",
    q: "วันครบกำหนดยื่นภาษีออนไลน์กับกระดาษเหมือนกันหรือไม่",
    a: `ไม่ควรสมมติว่าเหมือนกัน เพราะกรมสรรพากรอาจกำหนดวันครบกำหนดและสิทธิขยายเวลาต่างกันตามแบบและช่องทางยื่น อีกทั้งวันหยุดราชการหรือประกาศเฉพาะช่วงอาจกระทบกำหนดเวลา ให้ตรวจปฏิทินภาษีและประกาศปัจจุบันที่ rd.go.th ก่อนยื่นทุกเดือน ${ASOF}`,
    synonyms: ["กำหนดยื่นภาษีออนไลน์", "ปฏิทินภาษีกรมสรรพากร"],
  },
  {
    slug: "pan-card-from-thailand",
    q: "คนไทยหรือชาวต่างชาติสมัคร PAN Card อินเดียจากประเทศไทยได้หรือไม่",
    a: `ผู้ที่มีเหตุจำเป็นด้านภาษีหรือธุรกรรมในอินเดียอาจยื่นคำขอ PAN จากต่างประเทศได้ โดยต้องเลือกแบบคำขอให้ตรงสถานะ เตรียมหลักฐานตัวตน ที่อยู่ และเอกสารรับรองตามข้อกำหนดล่าสุด ช่องทางและรูปแบบการรับรองเอกสารต้องตรวจที่ incometax.gov.in และ protean-tinpan.com ก่อนส่ง ${PAN_CARD_NOTE} ${ASOF}`,
    synonyms: ["สมัคร PAN Card จากไทย", "Indian PAN non resident Thailand"],
    serviceSlug: "india-pan-card",
  },
  {
    slug: "pan-not-citizenship",
    q: "PAN Card เป็นบัตรประชาชนหรือหลักฐานสัญชาติอินเดียหรือไม่",
    a: `ไม่ใช่ PAN เป็นหมายเลขที่ใช้เชื่อมโยงข้อมูลด้านภาษีของอินเดีย ไม่ใช่หลักฐานสัญชาติ สิทธิพำนัก หรือใบอนุญาตทำงาน และไม่ควรโฆษณาว่าการได้ PAN จะทำให้เปิดบัญชีหรือทำธุรกรรมสำเร็จโดยอัตโนมัติ ${PAN_CARD_NOTE} ${ASOF}`,
    synonyms: ["PAN คือบัตรประชาชนไหม", "PAN card proof of citizenship"],
    serviceSlug: "india-pan-card",
  },
];

function serviceQA(service: (typeof ACCOUNTING_SERVICES)[number]): AccountingQA[] {
  const editorial = ACCOUNTING_EDITORIAL[service.slug];
  if (!editorial) return [];
  const base = [
    ["what", `${service.nameTh} คืออะไรและครอบคลุมงานใด`, `${editorial.decision} ผลส่งมอบที่ต้องตกลง: ${editorial.deliverables.join(" · ")} ${ASOF}`],
    ["documents", `${service.nameTh} ต้องเตรียมเอกสารอะไร`, `${editorial.documents.join(" · ")} รายการอาจเพิ่มตามข้อเท็จจริงของงาน ${ASOF}`],
    ["steps", `${service.nameTh} ควรเริ่มต้นอย่างไร`, `${editorial.steps.map(s => s.text).join(" จากนั้น ")} ${ASOF}`],
    ["risks", `${service.nameTh} มีข้อควรระวังอะไร`, `${editorial.limits.join(" · ")} ${ASOF}`],
    ["foreign", `${service.nameTh} สำหรับบริษัทต่างชาติต่างจากบริษัทไทยอย่างไร`, `ต้องตรวจว่าผู้ขอรับบริการและธุรกรรมอยู่ในขอบเขตงานนี้หรือไม่ ไม่ใช่ทุกบริการใช้กับบริษัทต่างชาติในลักษณะเดียวกัน ${editorial.decision} ${ASOF}`],
    ["quote", `${service.nameTh} คิดค่าบริการอย่างไร`, `${editorial.faqs[1]?.a ?? ACCOUNTING_QUOTE_NOTE} ${ASOF}`],
  ] as const;
  return base.map(([suffix, q, a]) => ({
    slug: `${service.slug}-${suffix}`,
    q,
    a,
    synonyms: [`${service.nameTh} ${suffix}`, `${service.nameEn.toLowerCase()} ${suffix}`],
    serviceSlug: service.slug,
  }));
}

function matrixQA(): AccountingQA[] {
  const out: AccountingQA[] = [];
  for (const service of ACCOUNTING_SERVICES) {
    for (const industry of ACCOUNTING_INDUSTRIES) {
      for (const intent of ["เอกสาร", "ขั้นตอน", "ตรวจย้อนหลัง"] as const) {
        out.push({
          slug: `${service.slug}-${industry.slug}-${intent === "เอกสาร" ? "docs" : intent === "ขั้นตอน" ? "process" : "review"}`,
          q: `${service.nameTh} สำหรับ${industry.nameTh} เรื่อง${intent}ควรตรวจอะไร`,
          a: `สำหรับ${industry.nameTh} ประเด็นเฉพาะที่ต้องพิจารณาคือ ${industry.notes} ก่อนดำเนิน${service.nameTh}ควรกระทบยอดเอกสารต้นทางกับบัญชี ธนาคาร สัญญา และแบบที่เคยยื่น แยกรายการที่ขาดหลักฐานหรือมีข้อสงสัยเพื่อทบทวนตาม ${service.legalBasis} ${ASOF}`,
          synonyms: [`${service.nameTh} ${industry.nameTh}`, `${service.nameEn} ${industry.nameEn}`],
          serviceSlug: service.slug,
        });
      }
    }
  }
  return out;
}

export const ACCOUNTING_QA_ALL: AccountingQA[] = (() => {
  const all = [...CORE, ...ACCOUNTING_SERVICES.flatMap(serviceQA), ...matrixQA()];
  const seen = new Set<string>();
  return all.filter((x) => (seen.has(x.slug) ? false : (seen.add(x.slug), true))).slice(0, 1000);
})();

liftFlatToFloor(ACCOUNTING_QA_ALL, {
  prefix: "accounting-t7",
  asOf: ASOF,
  authorityTh: "กรมสรรพากร กรมพัฒนาธุรกิจการค้า และหน่วยงานที่เกี่ยวข้อง",
  clusterTh: "งานบัญชี ภาษี และเงินเดือน",
  primary: ACCOUNTING_TOPUP_AXIS,
  floor: 3200,
});

/** Hand-written core + per-service editorial answers; matrix/top-up rows are excluded from the sitemap. */
export const ACCOUNTING_QA_EDITORIAL: AccountingQA[] = [...CORE, ...ACCOUNTING_SERVICES.flatMap(serviceQA)];

export const ACCOUNTING_QA_BY_SLUG: Record<string, AccountingQA> = Object.fromEntries(
  ACCOUNTING_QA_ALL.map((x) => [x.slug, x]),
);